import { useEffect, useState } from "react";
import { fetchDealershipsByDN } from "../api/fetchDealerships";

const dealershipCityCache = new Map<string, string>();
const dealershipCityPending = new Map<string, Promise<string>>();

const formatDealershipCityState = (city?: string | null, state?: string | null) => {
  const normalizedCity = city?.trim() ?? "";
  const normalizedState = state?.trim() ?? "";

  if (normalizedCity && normalizedState) {
    return `${normalizedCity}/${normalizedState}`;
  }

  return normalizedCity || normalizedState;
};

export const getDealershipCityByDnCached = async (dn: string): Promise<string> => {
  const normalizedDn = dn.trim();

  if (!normalizedDn) return "";

  const cachedCity = dealershipCityCache.get(normalizedDn);
  if (cachedCity) return cachedCity;

  const pendingRequest = dealershipCityPending.get(normalizedDn);
  if (pendingRequest) return pendingRequest;

  const request = fetchDealershipsByDN(normalizedDn)
    .then((dealership) => {
      const cityState = formatDealershipCityState(
        dealership?.city,
        dealership?.state
      );

      if (cityState) {
        dealershipCityCache.set(normalizedDn, cityState);
      }

      return cityState;
    })
    .catch(() => "")
    .finally(() => {
      dealershipCityPending.delete(normalizedDn);
    });

  dealershipCityPending.set(normalizedDn, request);

  return request;
};

export const useDealershipCityByDn = (
  dealershipDn?: string,
  fallbackCity?: string
) => {
  const [city, setCity] = useState(fallbackCity ?? "-");

  useEffect(() => {
    let isMounted = true;
    const dn = dealershipDn?.trim();
    const fallback = fallbackCity?.trim() || "-";

    setCity(fallback);

    if (!dn) return;

    const cachedCity = dealershipCityCache.get(dn);
    if (cachedCity) {
      setCity(cachedCity);
      return;
    }

    getDealershipCityByDnCached(dn).then((resolvedCity) => {
      if (isMounted && resolvedCity) {
        setCity(resolvedCity);
      }
    });

    return () => {
      isMounted = false;
    };
  }, [dealershipDn, fallbackCity]);

  return city;
};
